import { useMemo } from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAtomValue } from 'jotai';

import { Button as ButtonV2, Heading } from '@restworld/ui-ds-base';
import { GetMissedOrgApplicationActivities } from '@restworld/data-services';
import { cn, getAssetSrc, customImageLoader } from '@restworld/utils-common';
import { SkeletonEmployerCardLoading } from '@restworld/ui-components';
import { EmployerActivityCard } from '../legacy';

import { infiniteActivitiesQueryResponseAtom } from './hooks/use-missed-organisation-application-activities-query';

const Spotlight = dynamic(
  () => import('../../../../components/motion-primitives/spotlight').then((mod) => mod.Spotlight),
  { ssr: false }
);

const GlowEffect = dynamic(
  () => import('../../../../components/motion-primitives/glow-effect').then((mod) => mod.GlowEffect),
  { ssr: false }
);

function ActivityList() {
  const { t } = useTranslation('dashboard-page');
  const { activities, hasNextPage, query } = useAtomValue(infiniteActivitiesQueryResponseAtom);
  const isEmpty = useMemo(() => !activities || activities?.length === 0, [activities]);
  if (query.isLoading) {
    return <SkeletonEmployerCardLoading />;
  }

  if (isEmpty) {
    return <EmptyActivityList />;
  }

  return (
    <SectionHighlightWrapper>
      <GlowEffect
        colors={['#f0ebf8', '#9876d1', '#fae6e9', '#b9a1e0']}
        mode="flowHorizontal"
        blur={8}
        duration={5}
        scale={0.99}
      />
      <ActivityListBorder>
        <Spotlight
          gradient="radial-gradient(circle at center, #6633bb, #855cc9, #9876d1, transparent 80%)"
          size={700}
          springOptions={{ stiffness: 300, damping: 30 }}
        />
        <ActivityListWrapper>
          {activities.map((activity, i) => (
            <ActivityItem key={i} activity={activity} isFirst={i === 0} isLast={i === activities.length - 1} />
          ))}
          {hasNextPage && (
            <div className="flex justify-center pt-16">
              <ButtonV2
                StartIcon={PlusIcon}
                variant="tertiary"
                onClick={() => query.fetchNextPage()}
                loading={query.isFetchingNextPage}
              >
                {t('load_more_items')}
              </ButtonV2>
            </div>
          )}
        </ActivityListWrapper>
      </ActivityListBorder>
    </SectionHighlightWrapper>
  );
}
export default ActivityList;

function ActivityItem({
  activity: {
    applicant_avatars,
    application_count,
    category,
    city,
    occupation_name,
    restaurant_name,
    updated_at,
    job_position_id,
    restaurant_id,
  },
  isFirst,
  isLast,
}: ActivityItemProps) {
  const candidates = useMemo(
    () =>
      applicant_avatars?.map((candidate) => ({
        firstName: candidate.name,
        lastName: candidate.surname,
        image: candidate.avatar_url,
      })),
    [applicant_avatars]
  );
  const href = useMemo(
    () =>
      category === 'spontaneous_applications'
        ? `/employer/spontaneous-applications?restaurant_id=${restaurant_id}`
        : `/employer/job-positions/${job_position_id}`,
    [category, job_position_id, restaurant_id]
  );

  return (
    <EmployerActivityCard
      href={href}
      variant={category}
      candidateCount={application_count}
      city={city}
      jobPositionTitle={occupation_name}
      restaurantName={restaurant_name}
      dateSinceLastActivity={updated_at}
      candidates={candidates}
      isFirst={isFirst}
      isLast={isLast}
    />
  );
}

function EmptyActivityList() {
  const { t } = useTranslation('dashboard-page');

  return (
    <SectionHighlightWrapper>
      <EmptyListWrapper>
        <Heading
          size={5}
          weight="medium"
          color="neutral-light"
          className="w-full md:max-w-[600px] mb-2 mx-auto text-center"
        >
          {t('empty_activities_message')}
        </Heading>
        <ImageWrapper>
          <Image fill src={getAssetSrc('/utils-assets/person-with-trophy.svg')} alt="" loader={customImageLoader} />
        </ImageWrapper>
      </EmptyListWrapper>
    </SectionHighlightWrapper>
  );
}

interface ActivityItemProps {
  activity: GetMissedOrgApplicationActivities;
  isLast?: boolean;
  isFirst?: boolean;
}

const ActivityListBorder = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('relative overflow-hidden rounded-2xl bg-purple-50 p-px', className)} {...props} />
);
const ActivityListWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('relative overflow-hidden rounded-2xl bg-neutral-10', className)} {...props} />
);
const EmptyListWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('p-10 border border-purple-700 rounded-lg bg-neutral-10', className)} {...props} />
);
const ImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full sm:w-[126px] h-[150px] relative mx-auto', className)} {...props} />
);
const SectionHighlightWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('relative rounded-2xl', className)} {...props} />
);
